/**
 * Demand Health Overview Page
 * Extracted from App.js - Phase 7F Frontend Refactor
 * Shows overall demand strength and credibility breakdown
 */
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

const DemandHealthPage = () => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiFetch("/demand/scores/summary", { method: "GET" });
        setSummary(res.data);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
        toast.error("Failed to load demand data");
      }
    };
    fetchData();
  }, [apiFetch, toast]);

  const getDemandLevelColor = (level) => {
    switch (level) {
      case "high": return "#10b981";
      case "strong": return "#3b82f6";
      case "emerging": return "#f59e0b";
      case "low": return "#6b7280";
      default: return "#6b7280";
    }
  };

  const getDemandLevelBg = (level) => {
    switch (level) {
      case "high": return "#dcfce7";
      case "strong": return "#dbeafe";
      case "emerging": return "#fef3c7";
      case "low": return "#f3f4f6";
      default: return "#f3f4f6";
    }
  };

  if (isLoading) {
    return <div className="page-container"><div className="loading-spinner">Loading demand health...</div></div>;
  }

  // Handle no data / zero leads scenario gracefully
  const hasData = summary && (summary.unique_visitors > 0 || summary.total_events > 0);
  const distribution = summary?.demand_level_distribution || { low: 0, emerging: 0, strong: 0, high: 0 };
  const totalVisitors = summary?.unique_visitors || 0;
  const verifiedHighDemand = summary?.verified_high_demand_count || 0;
  const verifiedLeads = summary?.verified_leads || 0;
  const unverifiedVisitors = summary?.unverified_visitors || 0;
  const overallLevel = summary?.overall_demand_level || "low";
  const overallLabel = summary?.overall_demand_label || "Low Demand";
  const overallExplanation = summary?.overall_explanation || "No buyer signals detected yet. Demand data will appear here as visitors interact with your listings.";

  // Calculate max count for proportional bars
  const distributionValues = Object.values(distribution);
  const maxCount = Math.max(...distributionValues, 1);

  return (
    <div className="page-container demand-health-page">
      <div className="page-header">
        <h2>Demand Health</h2>
        <p className="page-subtitle">How strong and credible is buyer demand on your website right now?</p>
      </div>

      {/* Overall Demand Hero */}
      <div className="demand-overview-card" style={{ backgroundColor: getDemandLevelBg(overallLevel) }}>
        <div className="demand-overview-header">
          <span className="demand-level-badge" style={{ backgroundColor: getDemandLevelColor(overallLevel) }}>
            {overallLabel}
          </span>
          {verifiedHighDemand > 0 && (
            <span className="verified-badge">
              <span className="verified-badge-icon">✓</span>
              {verifiedHighDemand} Verified Demand
            </span>
          )}
        </div>
        <p className="demand-overview-explanation">{overallExplanation}</p>
      </div>

      {/* Demand Level Distribution */}
      <div className="demand-section">
        <div className="section-header">
          <h3>Demand Level Distribution</h3>
          <span className="section-total">{totalVisitors} visitors tracked</span>
        </div>
        <div className="demand-levels-grid">
          {[
            { key: "high", label: "High", description: "Ready to buy" },
            { key: "strong", label: "Strong", description: "Seriously interested" },
            { key: "emerging", label: "Emerging", description: "Showing interest" },
            { key: "low", label: "Low", description: "Just browsing" }
          ].map(({ key, label, description }) => {
            const count = distribution[key] || 0;
            const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
            return (
              <div key={key} className="demand-level-row">
                <div className="demand-level-info">
                  <span className="demand-level-name" style={{ color: getDemandLevelColor(key) }}>{label}</span>
                  <span className="demand-level-desc">{description}</span>
                </div>
                <div className="demand-level-bar-wrap">
                  <div 
                    className="demand-level-bar" 
                    style={{ 
                      width: `${Math.max(barWidth, count > 0 ? 4 : 0)}%`,
                      backgroundColor: getDemandLevelColor(key)
                    }}
                  />
                </div>
                <span className="demand-level-count">{count}</span>
              </div>
            );
          })}
        </div>
        {!hasData && (
          <div className="empty-hint">
            Visitor demand levels will appear here once buyers interact with your listings.
          </div>
        )}
      </div>

      {/* Verified vs Unverified Comparison */}
      <div className="demand-section">
        <div className="section-header">
          <h3>Demand Credibility</h3>
        </div>
        <p className="section-description">Compare verified buyers against anonymous visitors showing intent.</p>
        <div className="credibility-comparison">
          <div className="credibility-block verified-block">
            <div className="credibility-block-header">
              <span className="credibility-check-icon">✓</span>
              <span>Verified Demand</span>
            </div>
            <div className="credibility-block-value">{verifiedLeads}</div>
            <div className="credibility-block-label">Phone-verified buyers</div>
            {verifiedHighDemand > 0 && (
              <div className="credibility-highlight">
                <span className="highlight-number">{verifiedHighDemand}</span> with high demand signals
              </div>
            )}
          </div>
          <div className="credibility-divider">
            <span>vs</span>
          </div>
          <div className="credibility-block unverified-block">
            <div className="credibility-block-header">
              <span className="credibility-anon-icon">?</span>
              <span>Unverified Demand</span>
            </div>
            <div className="credibility-block-value">{unverifiedVisitors}</div>
            <div className="credibility-block-label">Anonymous visitors with intent</div>
          </div>
        </div>
        {!hasData && (
          <div className="empty-hint">
            Credibility data will appear as leads verify their phone numbers.
          </div>
        )}
      </div>

      {/* Verified Demand Callout - Only show if > 0 */}
      {verifiedHighDemand > 0 && (
        <div className="demand-callout">
          <div className="callout-icon">🔥</div>
          <div className="callout-content">
            <div className="callout-title">Verified Demand</div>
            <div className="callout-text">
              You have <strong>{verifiedHighDemand}</strong> verified buyer{verifiedHighDemand !== 1 ? 's' : ''} showing strong buying signals. 
              These are your highest-confidence prospects.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandHealthPage;
